// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
export { PERMIT_META, SEPARATE_META, DATA_ORIGIN_META, DATA_REACH_META, PRECOMMIT_META, RES_CONSUME_META, RES_RELEASE_META, COMPOSE_META, INV_ESTABLISH_META, INV_PRESERVE_META, } from "@csk/core";
import { PERMIT_META, SEPARATE_META, DATA_ORIGIN_META, DATA_REACH_META, PRECOMMIT_META, RES_CONSUME_META, RES_RELEASE_META, COMPOSE_META, INV_ESTABLISH_META, INV_PRESERVE_META, } from "@csk/core";
/**
 * Theorem metadata for all 10 CSK neutralization theorems, keyed by
 * canonical theorem code.
 *
 * These values are derived from `tooling/theorems/registry.py` (the
 * canonical registry). Both `@csk/core` and `@csk/wasm` expose this
 * metadata; the values are identical — sharing them is the seam bridge.
 */
// inline: local
export const WASM_THEOREM_META = {
    D1: {
        code: "D1",
        name: "DATA.ORIGIN",
        legacy: "INFO",
        structure: "Fibration",
        safetyClass: "compositional",
        mode: "immune",
        tier: 2,
        seamType: "vertical",
        generator: "G2",
        agdaSource: "Kernel/DATA_ORIGIN/Neutralization.agda",
    },
    D2: {
        code: "D2",
        name: "DATA.REACH",
        legacy: "",
        structure: "Opfibration",
        safetyClass: "compositional",
        mode: "immune",
        tier: 2,
        seamType: "horizontal",
        generator: "G2",
        agdaSource: "Kernel/DATA_REACH/Neutralization.agda",
    },
    P: {
        code: "P",
        name: "PERMIT",
        legacy: "AUTH",
        structure: "Slice category",
        safetyClass: "compositional",
        mode: "immune",
        tier: 1,
        seamType: "mixed",
        generator: "G1",
        agdaSource: "Kernel/PERMIT/Neutralization.agda",
    },
    S: {
        code: "S",
        name: "SEPARATE",
        legacy: "BOUND",
        structure: "Double category",
        safetyClass: "compositional",
        mode: "immune",
        tier: 2,
        seamType: "diagonal",
        generator: "G2",
        agdaSource: "Kernel/SEPARATE/Neutralization.agda",
    },
    W: {
        code: "W",
        name: "PRECOMMIT",
        legacy: "IRREV",
        structure: "Graded monad",
        safetyClass: "compositional",
        mode: "immune",
        tier: 1,
        seamType: "vertical",
        generator: "G3",
        agdaSource: "Kernel/PRECOMMIT/Neutralization.agda",
    },
    R1: {
        code: "R1",
        name: "RES.CONSUME",
        legacy: "RES",
        structure: "Linear category",
        safetyClass: "parametric",
        mode: "contained",
        tier: 3,
        seamType: "horizontal",
        generator: "G4",
        agdaSource: "Kernel/RES_CONSUME/Neutralization.agda",
    },
    R2: {
        code: "R2",
        name: "RES.RELEASE",
        legacy: "",
        structure: "Compact closed",
        safetyClass: "parametric",
        mode: "contained",
        tier: 3,
        seamType: "horizontal",
        generator: "G4",
        agdaSource: "Kernel/RES_RELEASE/Neutralization.agda",
    },
    C: {
        code: "C",
        name: "COMPOSE",
        legacy: "COMP",
        structure: "Operad",
        safetyClass: "parametric",
        mode: "inert",
        tier: 1,
        seamType: "horizontal",
        generator: "G1",
        agdaSource: "Kernel/COMPOSE/Neutralization.agda",
    },
    I1: {
        code: "I1",
        name: "INV.ESTABLISH",
        legacy: "",
        structure: "F-algebra",
        safetyClass: "parametric",
        mode: "inert",
        tier: 2,
        seamType: "vertical",
        generator: "G3",
        agdaSource: "Kernel/INV_ESTABLISH/Neutralization.agda",
    },
    I2: {
        code: "I2",
        name: "INV.PRESERVE",
        legacy: "TEMP",
        structure: "F-coalgebra",
        safetyClass: "parametric",
        mode: "inert",
        tier: 2,
        seamType: "horizontal",
        generator: "G3",
        agdaSource: "Kernel/INV_PRESERVE/Neutralization.agda",
    },
};
/** All theorem codes in canonical order. */
export const WASM_THEOREM_CODES = Object.keys(WASM_THEOREM_META);
// ── CORE_THEOREM_META ─────────────────────────────────────────
/**
 * The actual `GuaranteeMeta` objects from `@csk/core`, keyed by theorem code.
 *
 * This is the authoritative record: these objects come directly from
 * `@csk/core`'s theorem modules. `WASM_THEOREM_META` mirrors these values
 * for consumers that do not depend on `@csk/core`.
 *
 * Use `verifyRealizationAlignment()` to assert that the two records agree.
 */
// inline: local
export const CORE_THEOREM_META = {
    D1: DATA_ORIGIN_META,
    D2: DATA_REACH_META,
    P: PERMIT_META,
    S: SEPARATE_META,
    W: PRECOMMIT_META,
    R1: RES_CONSUME_META,
    R2: RES_RELEASE_META,
    C: COMPOSE_META,
    I1: INV_ESTABLISH_META,
    I2: INV_PRESERVE_META,
};
// inline: local
const COMPARED_FIELDS = [
    "code",
    "name",
    "legacy",
    "structure",
    "safetyClass",
    "mode",
    "tier",
    "seamType",
    "generator",
];
/**
 * Verify that `WASM_THEOREM_META` and `CORE_THEOREM_META` agree on all
 * shared fields for all 10 theorems.
 *
 * This is the runtime witness that both realizations implement the same
 * canonical theorem specifications. A mismatch indicates that one of the
 * two sources has drifted from `tooling/theorems/registry.py`.
 *
 * @returns An `AlignmentResult` with `aligned: true` if all fields match.
 *
 * @example
 * ```typescript
 * import { verifyRealizationAlignment } from '@csk/wasm/meta';
 * const result = verifyRealizationAlignment();
 * if (!result.aligned) {
 *   console.error('Realization drift:', result.mismatches);
 * }
 * ```
 */
export function verifyRealizationAlignment() {
    const mismatches = [];
    for (const code of Object.keys(WASM_THEOREM_META)) {
        const wasmEntry = WASM_THEOREM_META[code];
        const coreEntry = CORE_THEOREM_META[code];
        if (!wasmEntry || !coreEntry) {
            mismatches.push({
                code,
                field: "existence",
                wasmValue: wasmEntry ? "present" : "missing",
                coreValue: coreEntry ? "present" : "missing",
            });
            continue;
        }
        for (const field of COMPARED_FIELDS) {
            const wasmValue = wasmEntry[field];
            const coreValue = coreEntry[field];
            if (wasmValue !== coreValue) {
                mismatches.push({ code, field, wasmValue, coreValue });
            }
        }
    }
    return { aligned: mismatches.length === 0, mismatches };
}
//# sourceMappingURL=meta.js.map